import { CustomReportCategory, OffsetUnit } from '../../../../../redux/context/customReports/typesCategory'
import { ReportDataType } from '../../../../../redux/context/reports/types'
import {
  RowType,
  ThresholdValueSetting,
  CustomReportVariable
} from '../../../../../redux/context/customReports/typesVariable'
import { TimePeriodOptions } from '../../../../../redux/context/customReports/types'
import { ReportGrammarRules } from '../../../../../redux/config/customReport/types'
import { populateTreeFromData } from '../category/Builder/treeImporter'
import { NormalizedValidationVisitor } from '../category/Builder/normalizedVisitors/NormalizedValidationVisitor'
import { INFOBOX_IDS } from './infoboxConstants'
import { InfoboxFormValues, InfoboxComparison, InfoboxCompanySelection } from './types'
import { resolveEffectiveFlags } from './infoboxEffectiveFlags'

let idCounter = 0
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().substring(0, 8)
  }
  idCounter += 1
  return `gen-${idCounter.toString(36)}`
}

function buildComparisonPeriodGroup(
  comparison: InfoboxComparison,
  periodValue: string,
  company: InfoboxCompanySelection
): Partial<CustomReportCategory> {
  switch (comparison) {
    case 'prevMonth':
      return { value: periodValue, dataType: ReportDataType.actuals, offset: -1, offsetUnit: OffsetUnit.month }
    case 'prevYear':
      return { value: periodValue, dataType: ReportDataType.actuals, offset: -12, offsetUnit: OffsetUnit.month }
    case 'budget':
      return {
        value: periodValue,
        dataType: ReportDataType.budget,
        budgetingScenarioId: company.budgetingScenarioId ?? undefined
      }
    case 'forecast':
      return {
        value: periodValue,
        dataType: ReportDataType.forecast,
        budgetingScenarioId: company.budgetingScenarioId ?? undefined
      }
    default:
      return { value: periodValue, dataType: ReportDataType.actuals }
  }
}

export interface InfoboxCategoryArgs {
  companyArgumentId?: number
  dimensionArgumentId?: number
}

function makeDynamicCompanyNode(
  form: InfoboxFormValues,
  args: InfoboxCategoryArgs,
  periodGroupFactory: (company: InfoboxCompanySelection) => Partial<CustomReportCategory>
): CustomReportCategory {
  const emptyCompany: InfoboxCompanySelection = { id: '', name: '' }
  const periodGroupNode: Partial<CustomReportCategory> = {
    id: generateId(),
    type: 'periodGroup',
    budgetingScenarioId: null,
    ...periodGroupFactory(emptyCompany)
  }

  const includeDimension = form.dynamicScope === 'companyAndDimension'
  const companyChildren: Partial<CustomReportCategory>[] = includeDimension
    ? [
        {
          id: generateId(),
          type: 'dimension' as const,
          argumentId: args.dimensionArgumentId,
          children: [periodGroupNode as CustomReportCategory]
        }
      ]
    : [periodGroupNode]

  return {
    id: generateId(),
    type: 'company' as const,
    argumentId: args.companyArgumentId,
    children: companyChildren as CustomReportCategory[]
  }
}

function makeCompanyNodes(
  form: InfoboxFormValues,
  args: InfoboxCategoryArgs,
  periodGroupFactory: (company: InfoboxCompanySelection) => Partial<CustomReportCategory>
): CustomReportCategory[] {
  if (form.dynamicScope !== 'none') {
    return [makeDynamicCompanyNode(form, args, periodGroupFactory)]
  }

  return form.companyScope.map(company => {
    const makePeriodGroupNode = (): Partial<CustomReportCategory> => ({
      id: generateId(),
      type: 'periodGroup',
      budgetingScenarioId: null,
      ...periodGroupFactory(company)
    })

    const companyChildren: Partial<CustomReportCategory>[] = company.dimensions?.length
      ? company.dimensions.map((dimId: string) => ({
          id: generateId(),
          type: 'dimension' as const,
          value: dimId,
          children: [makePeriodGroupNode() as CustomReportCategory]
        }))
      : [makePeriodGroupNode()]

    return {
      id: generateId(),
      type: 'company' as const,
      value: company.id,
      children: companyChildren as CustomReportCategory[]
    }
  })
}

function validateCategories(categories: CustomReportCategory[], rules: ReportGrammarRules): void {
  const { nodesById, rootId } = populateTreeFromData(categories)
  const validator = new NormalizedValidationVisitor(rules, nodesById)
  const errors = validator.validate(rootId)
  if (errors.length > 0) {
    const referenceErrors = errors.filter(e => e.message.includes('referenceNotFound'))
    if (referenceErrors.length > 0) {
      throw new Error(`Infobox category builder: orphan references found (${referenceErrors.length})`)
    }
  }
}

export function buildInfoboxCategories(
  form: InfoboxFormValues,
  args: InfoboxCategoryArgs = {},
  rules?: ReportGrammarRules
): CustomReportCategory[] {
  const flags = resolveEffectiveFlags(form)
  const periodValue = form.period
  const categories: CustomReportCategory[] = []

  // 1. Actual — always present
  categories.push({
    id: INFOBOX_IDS.actual,
    type: 'function',
    value: 'sum',
    title: 'actual',
    children: makeCompanyNodes(form, args, () => ({ value: periodValue, dataType: ReportDataType.actuals }))
  })

  // 2. Comparison
  if (form.comparison !== 'none') {
    categories.push({
      id: INFOBOX_IDS.comparison,
      type: 'function',
      value: 'sum',
      title: 'comparison',
      children: makeCompanyNodes(form, args, company =>
        buildComparisonPeriodGroup(form.comparison, periodValue, company)
      )
    })
  }

  // 3. Growth
  if (flags.showGrowth) {
    categories.push({
      id: INFOBOX_IDS.growth,
      type: 'function',
      value: 'growth',
      title: 'growth',
      children: [
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.actual },
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comparison }
      ]
    })
  }

  // 4. Diff
  if (flags.showDiff) {
    categories.push({
      id: INFOBOX_IDS.diff,
      type: 'function',
      value: 'diff',
      title: 'diff',
      children: [
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.actual },
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comparison }
      ]
    })
  }

  // 5. Combo actual + comparison
  if (flags.showCombo) {
    categories.push({
      id: INFOBOX_IDS.comboActual,
      type: 'function',
      value: 'sum',
      title: 'combo-actual',
      children: makeCompanyNodes(form, args, () => ({
        value: TimePeriodOptions.CurrentMonth,
        dataType: ReportDataType.actuals
      }))
    })

    if (form.comparison !== 'none') {
      categories.push({
        id: INFOBOX_IDS.comboComparison,
        type: 'function',
        value: 'sum',
        title: 'combo-comparison',
        children: makeCompanyNodes(form, args, company =>
          buildComparisonPeriodGroup(form.comparison, TimePeriodOptions.CurrentMonth, company)
        )
      })
    }
  }

  // 6. Combo growth
  if (flags.showComboGrowth) {
    categories.push({
      id: INFOBOX_IDS.comboGrowth,
      type: 'function',
      value: 'growth',
      title: 'combo-growth',
      children: [
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comboActual },
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comboComparison }
      ]
    })
  }

  // 7. Combo diff
  if (flags.showComboDiff) {
    categories.push({
      id: INFOBOX_IDS.comboDiff,
      type: 'function',
      value: 'diff',
      title: 'combo-diff',
      children: [
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comboActual },
        { id: generateId(), type: 'reference', value: INFOBOX_IDS.comboComparison }
      ]
    })
  }

  // 8. Sparkline — 12kk historia raportoitavaan kuukauteen
  if (flags.showSparkline) {
    for (let i = 0; i < 12; i += 1) {
      categories.push({
        id: INFOBOX_IDS.sparkline(i),
        type: 'function',
        value: 'sum',
        title: `sparkline-${i}`,
        children: makeCompanyNodes(form, args, () => ({
          value: TimePeriodOptions.CurrentMonth,
          offset: i - 11,
          offsetUnit: OffsetUnit.month,
          dataType: ReportDataType.actuals
        }))
      })
    }
  }

  if (rules) {
    validateCategories(categories, rules)
  }

  return categories
}

export function buildInfoboxVariables(form: InfoboxFormValues): Partial<CustomReportVariable>[] {
  const style = {
    numberFormat: { decimals: form.decimals, showUnit: form.showUnit },
    thresholdValueSetting: form.thresholdValueSetting ?? ThresholdValueSetting.unset,
    color: form.color
  }

  switch (form.rowType) {
    case RowType.formula:
      return [{ rowType: RowType.formula, formulaId: form.formulaId!, style } as CustomReportVariable]
    case RowType.source:
      return [{ rowType: RowType.source, source: form.source!, type: form.sourceType, style } as CustomReportVariable]
    default:
      return [{ rowType: RowType.keyFigure, keyFigureId: form.keyFigureId!, style } as CustomReportVariable]
  }
}
